import React, { useContext, useEffect, useState } from "react";

// import i18n
import { useTranslation } from "react-i18next";

// import material UI
import { Typography, FormControl, Box, CircularProgress, circularProgressClasses } from "@mui/material";

// import react router dom
import { Outlet } from "react-router-dom";

// import context
import { AppContext } from "../../App";

// import component
import DivFlexCenter from "../DivFlexCenter";
import DivFlexSpaceBetween from "../DivFlexSpacebetween";
import CustomDialog from "./DialogContainer/CustomDialog";
import CustomDialogContent from "./DialogContainer/CustomDialogContent";
import TextFieldStyled from "../TextField/TextFieldStyle";

// import style and theme
import { useTheme } from "@mui/material/styles";

// import Constants
import { FontFamily } from "../../Constants/FontFamily";
import { RoleEnum } from "../../Constants/RoleEnum";
import ErrorMessage from "../ErrorMessage";
import { normalizeEmail } from "../../utils/util";

// import component
import DivFlexStart from "../DivFlexStart";
import CheckedIcon from "../../assets/icons/CheckedIcon";
import Button from "../Button";
import { CORE_ACTION_POST_CREATE_OUTLET_EMAIL_ADDRESSES } from "../../Actions/CoreActions";

const InviteUserDialog = (props) => {

    const { outletId, isMobile, isDesktop, userRole, setRolesOption, setOpenDialog } = useContext(AppContext)
    const { setIsApiLoading, isApiLoading, isOpen, outletList, setIsDisplayApiSuccesMessage, isDisplayApiSuccesMessage } = props;

    const { t } = useTranslation()

    const theme = useTheme()

    const initEmail = { id: null, email: '', role: RoleEnum.ADMIN }

    const [email, setEmail] = useState(initEmail)

    // validation for email
    const [isEmailEmpty, setIsEmailEmpty] = useState(false)
    const [isEmailInvalid, setIsEmailInvalid] = useState(false)
    const [isEmailExist, setIsEmailExist] = useState(false)
    const [isError, setIsError] = useState(false)

    function isValidEmail(email) {
        return /\S+@\S+\.\S+/.test(email);
    }

    const handleChangeInput = (e) => {
        const { name, value } = e.target
        setEmail({ ...email, [name]: value })
    }

    const handleSubmit = (e) => {
        setIsApiLoading(true)
        setIsEmailInvalid(false)
        setIsEmailExist(false)
        setIsEmailEmpty(false)
        setIsError(false)
        setIsDisplayApiSuccesMessage(false)
        
        if (email.email === '') {
            setIsEmailEmpty(true)
            setIsApiLoading(false)
        }
        else {
            if (isValidEmail(email.email)) {
                CORE_ACTION_POST_CREATE_OUTLET_EMAIL_ADDRESSES(
                    null,
                    true, // send email
                    {
                        id: null,
                        outlet_id: outletId,
                        email: email.email
                    },
                    res => {
                        if (res.status === 200) {
                            setIsDisplayApiSuccesMessage(true)

                            const defaultEmail = { id: null, email: '', role: RoleEnum.ADMIN }
                            setEmail(defaultEmail)
                            setIsApiLoading(false)
                        }
                    },
                    err => {
                        console.log(err)
                        if(err && err.response) {
                            if(err.response.data === 'EMAIL_ALREADY_EXISTS') {
                                setIsEmailExist(true)
                                setIsApiLoading(false)
                            }
                            else {
                                setIsError(true)
                                setIsApiLoading(false)
                            }
                        }
                    }
                )
            }
            else {
                setIsEmailInvalid(true)
                setIsApiLoading(false)
            }
        }
    }

    const handleCheckIfEmailExist = () => {
        let emailExist = false;
        let currentModifiedOutlet = outletList.filter((eachData) => eachData.id === outletId)

        currentModifiedOutlet[0].outlet_emails.forEach((eachData) => {
            if (normalizeEmail(eachData.email) === normalizeEmail(email.email)) {
                emailExist = true;
            }
        })

        return emailExist;
    }

    const handleCloseDialogAddNewEmail = () => {
        setEmail(initEmail)
        setIsEmailInvalid(false)
        setIsEmailExist(false)
        setIsEmailEmpty(false)
        setIsError(false)
        setOpenDialog("");
        setIsDisplayApiSuccesMessage(false);
    }

    useEffect(() => {
        const roles = [
            {
                value: RoleEnum.ADMIN,
                label: t("admin"),
            },
            {
                value: RoleEnum.SUPER_ADMIN,
                label: t("superAdmin"),
            },
            {
                value: RoleEnum.STANDARD,
                label: t("standard"),
            },
        ];

        const rolesOptionForAdmin = [
            {
                value: RoleEnum.ADMIN,
                label: t("admin"),
            },
            {
                value: RoleEnum.STANDARD,
                label: t("standard"),
            },
        ];

        if (userRole === RoleEnum.SUPER_ADMIN) {
            setRolesOption(roles)
        } else {
            setRolesOption(rolesOptionForAdmin)
        }
    }, [userRole])

    return (
        <>
            <CustomDialog width={900} open={isOpen} onClose={handleCloseDialogAddNewEmail} theme={theme} >
                <Box sx={{ backgroundColor: theme.palette.background.dialog }}>
                    <CustomDialogContent isMobile={isMobile} sx={{ padding: isMobile ? '12px 16px 24px 16px' : '0px 40px 64px 40px' }}>
                        <DivFlexCenter sx={{ height: isMobile ? 22 : 40, mb: isMobile ? 5 : 8 }}>
                            <Typography sx={{ color: theme.palette.text.primary, fontSize: isMobile ? 22 : 48, fontFamily: FontFamily.PLUSJAKARTASANSSEMIBOLD }}>
                                {t("inviteUser")}
                            </Typography>
                        </DivFlexCenter>
                        <FormControl sx={{ width: '100%' }} >
                            <DivFlexSpaceBetween sx={{ flexWrap: 'wrap', width: '100%', flexDirection: isMobile ? 'column' : 'row' }}>
                                <Box sx={{ display: 'flex', width: '100%', flexDirection: 'column', gap: isMobile ? 1 : 1.5, mb: (!isDesktop && !isEmailEmpty && !isEmailInvalid) && 2 }}>
                                    <Typography sx={{ fontSize: isMobile ? 14 : 24, fontFamily: FontFamily.PLUSJAKARTASANSSEMIBOLD, color: theme.palette.text.primary }}>
                                        {`${t("email")} *`}
                                    </Typography>
                                    <TextFieldStyled
                                        value={email.email}
                                        onChange={handleChangeInput}
                                        id="basic"
                                        placeholder="example@mail.com"
                                        name="email"
                                        sx={{ width: '100%' }}
                                        isMobile={isMobile}
                                        padding={isMobile ? '16px' : '28px 24px'}
                                        fontSize={isMobile ? 16 : 24}
                                        disabled={isApiLoading}
                                    />
                                </Box>

                                {!isDesktop && (isEmailEmpty || isEmailExist || isEmailInvalid || isError) &&
                                    <ErrorMessage
                                        isEmailEmpty={isEmailEmpty}
                                        isEmailInvalid={isEmailInvalid}
                                        isEmailExist={isEmailExist}
                                        isError={isError}
                                        isDesktop={isDesktop}
                                        isMobile={isMobile}
                                    />
                                }


                            </DivFlexSpaceBetween>
                        </FormControl>

                        {isDesktop && (isEmailEmpty || isEmailExist || isEmailInvalid) &&
                            <ErrorMessage
                                isEmailEmpty={isEmailEmpty}
                                isEmailInvalid={isEmailInvalid}
                                isEmailExist={isEmailExist}
                                isDesktop={isDesktop}
                                isMobile={isMobile}
                            />
                        }
                        {isDisplayApiSuccesMessage && (
                            <DivFlexStart sx={{ width: isMobile ? '100%' : '60%', mt: 1 }}>
                                <DivFlexStart>
                                    <CheckedIcon sx={{ color: 'green', fontSize: isMobile ? 12 : 24, mr: 1, mt: -0.4 }} />
                                    <Typography sx={{ fontSize: isMobile ? 12 : 20, fontFamily: FontFamily.PLUSJAKARTASANS }} color={'green'}>
                                        {t("emailSent")}
                                    </Typography>
                                </DivFlexStart>
                            </DivFlexStart>
                        )}

                        <DivFlexSpaceBetween sx={{ mt: isMobile ? 9.75 : 10, flexDirection: isMobile ? 'column-reverse' : 'row', gap: isMobile ? 1.5 : 0 }}>
                            <Typography sx={{ fontSize: isMobile ? 14 : 21, textDecoration: 'underline', fontFamily: FontFamily.PLUSJAKARTASANSSEMIBOLD, cursor: 'pointer', color: theme.palette.text.primary }} onClick={handleCloseDialogAddNewEmail} >
                                {t("cancel")}
                            </Typography>

                            <Button
                                isLoading={isApiLoading}
                                style={{ width: isMobile ? '100%' : '45%', py: isMobile ? '12px' : '16px', height: '' }}
                                onClick={handleSubmit}>
                                <Typography sx={{ fontFamily: FontFamily.PLUSJAKARTASANSSEMIBOLD, fontSize: isMobile ? 14 : 24 }}>
                                    {t("sendInvitation")}
                                </Typography>
                            </Button>
                        </DivFlexSpaceBetween>
                    </CustomDialogContent>
                </Box>
            </CustomDialog>
            <Outlet />
        </>

    )
}


export default InviteUserDialog